import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { resolveVestigasHttpErrorDetail } from '~/data/utils';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n } from '~/utils';

import { queryKeysAssetReparsing } from './queryKeys';

type ReparsingResponse = {
  code: number; // 0 / 1?
  message: string;
  requestId: string | undefined;
};

/**
 * Parses the asset with the specific ID again.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/reparse_asset_vadmin_asset_reparsing__reparsing_id__parse_post
 */
const reparseAsset = async (reparsingId: string) => {
  try {
    const response = await vestigasApi.post(
      ENDPOINT.ASSET_REPARSING.REPARSE_ASSET(reparsingId),
    );

    return {
      ...response,
      reparsingId,
    };
  } catch (error) {
    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 400: {
          errorMessage = i18n.t(
            'assetReparsing.mutation.reparseAsset.error.400',
          );
          break;
        }

        case 409: {
          errorMessage =
            resolveVestigasHttpErrorDetail(error) ||
            i18n.t(($) => $.assetReparsing.mutation.reparseAsset.error['409'], {
              ns: 'admin-webapp',
            });
          break;
        }

        case 422: {
          errorMessage = i18n.t(
            'assetReparsing.mutation.reparseAsset.error.422',
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            'assetReparsing.mutation.reparseAsset.error.generic',
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * A custom hook to mark all reparsing tasks on the asset as complete and reparse the asset. Also deletes the reparsing asset afterwards (which should be done automatically).
 * Uses the `useMutation` hook from `@tanstack/react-query` internally.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/reparse_asset_vadmin_asset_reparsing__reparsing_id__parse_post
 */
export const useMutationReparseAsset = (
  reparsingId: string,
  options?: UseMutationOptions,
): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reparsingIdArgument: UUID | undefined) =>
      reparseAsset(reparsingIdArgument || reparsingId), // TODO: it's wrong to pass the function args in through the hook!
    onError(error) {
      return; // prevent error from bubbling up further unless overridden via mutation options
    },
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: queryKeysAssetReparsing.getAll(),
      });
    },
    async onSuccess({
      reparsingId,
      status,
    }: {
      reparsingId: UUID;
      status: number;
    }) {
      let successMessage = '';
      switch (status) {
        case 202: {
          successMessage = i18n.t(
            'assetReparsing.mutation.reparseAsset.success.202',
          );
          break;
        }

        default: {
          successMessage = i18n.t(
            'assetReparsing.mutation.reparseAsset.success.generic',
          );
        }
      }

      ToastService.info(successMessage);
    },
    ...options,
  });
};
